/**
 * CrmHygieneCard — the governed CRM-hygiene superpower surface.
 *
 * Scans the live people + accounts snapshot with the deterministic
 * hygiene scanner, drafts each fix (real Claude worker when configured,
 * deterministic fallback otherwise — the honest source label rides every
 * row), clusters the drafts into one reversible approval bundle, and lets
 * the operator triage by risk band: approve a whole low-risk cluster in
 * one click, scrutinize high-risk fixes individually.
 *
 * NOTHING here writes directly: every approval routes the diff through
 * the existing governed pipeline (queueApproval → Approvals inbox →
 * resolveApproval), and "Rollback bundle" queues the exact inverse diffs
 * compiled by reversalPlan — undo is just another governed proposal, the
 * same contract reversal.ts establishes for ledger entries.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CircleDashed,
  RotateCcw,
  ScanSearch,
  Sparkles,
  Undo2,
  X,
  Zap,
} from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { useToast } from './ToastProvider';
import { C } from '../lib/tokens';
import { useAuth } from '../lib/auth/AuthProvider';
import { subscribePeople } from '../lib/db/people';
import { subscribeAccounts } from '../lib/db/accounts';
import { accountRollupStage, type Account, type Person } from '../lib/db/types';
import { queueApproval } from '../lib/agent/approvals';
import { REVERSAL_NOTE_PREFIX } from '../lib/agent/reversal';
import * as argoWorker from '../lib/agent/argoWorker';
import { fnvHash } from '../lib/ledger/verifyChain';
import type { AgentAction } from '../lib/agent/types';
import {
  scanHygiene,
  type HygieneAccount,
  type HygienePerson,
  type TouchEvent,
} from '../lib/crm/hygieneScanner';
import { draftAll, type DraftedFix } from '../lib/crm/hygieneDrafter';
import {
  approveCluster,
  buildBundle,
  rejectFinding,
  reversalPlan,
  type HygieneBundle,
} from '../lib/crm/hygieneBundle';
import type { ActionDiff } from '../lib/crm/inlineEditDiff';

// ---------------------------------------------------------------------------
// Adapters — live records → the scanner's pure input shapes
// ---------------------------------------------------------------------------

const STALE_DAYS = 14;

function toHygienePeople(people: Person[]): HygienePerson[] {
  return people
    .filter((p) => p.category === 'prospect' || p.category === 'customer')
    .map((p) => ({
      id: p.id,
      name: p.name,
      ...(p.email !== undefined ? { email: p.email } : {}),
      ...(p.company !== undefined ? { company: p.company } : {}),
      ...(p.stage !== undefined ? { stage: p.stage } : {}),
      // Honest proxy: stage-entry time is not tracked on Person, so the
      // last record update stands in for it (never fresher than reality).
      stageEnteredAt: Date.parse(p.updatedAt) || undefined,
      ...(p.nextStep !== undefined ? { nextStep: p.nextStep } : {}),
      ...(p.followUpDate !== undefined ? { followUpDate: p.followUpDate } : {}),
    }));
}

function toTouches(people: Person[]): TouchEvent[] {
  const touches: TouchEvent[] = [];
  for (const p of people) {
    for (const a of p.activity ?? []) {
      const at = Date.parse(a.at);
      if (Number.isFinite(at)) touches.push({ recordId: p.id, at });
    }
    if (p.lastTouch) {
      const at = Date.parse(p.lastTouch);
      if (Number.isFinite(at)) touches.push({ recordId: p.id, at });
    }
  }
  return touches;
}

function toHygieneAccounts(accounts: Account[]): HygieneAccount[] {
  return accounts.map((a) => {
    const stage = accountRollupStage(a);
    return {
      id: a.id,
      name: a.name,
      ...(stage !== undefined ? { stage } : {}),
      stageEnteredAt: Date.parse(a.updatedAt) || undefined,
    };
  });
}

/** Blast radius of one diff: field count, stage moves weigh 3. */
function blastRadiusOf(diff: ActionDiff): number {
  return diff.changes.reduce((n, c) => n + (c.attr === 'stage' ? 3 : 1), 0);
}

/** Map one hygiene diff onto governed update_record_field proposals. */
function toActions(
  diff: ActionDiff,
  people: Person[],
  accounts: Account[],
): AgentAction[] {
  const person = people.find((p) => p.id === diff.recordId);
  const account = person ? undefined : accounts.find((a) => a.id === diff.recordId);
  const recordType: 'person' | 'account' = person ? 'person' : 'account';
  const recordName = person?.name ?? account?.name ?? diff.recordId;
  return diff.changes.map((c) => ({
    kind: 'update_record_field',
    recordType,
    recordId: diff.recordId,
    recordName,
    attr: c.attr,
    from: c.from === null || c.from === undefined ? null : String(c.from),
    to: String(c.to ?? ''),
  }));
}

/** Ledger-style provenance stamp for one draft (same FNV-1a seal). */
function stampOf(d: DraftedFix): string {
  return fnvHash(
    '',
    JSON.stringify([d.finding.id, d.finding.evidence, d.rationale, d.source]),
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SEVERITY_PILL: Record<string, string> = {
  high: 'glass-pill glass-pill--orange',
  med: 'glass-pill',
  low: 'glass-pill glass-pill--green',
};

export function CrmHygieneCard({ compact = false }: { compact?: boolean }) {
  const { user } = useAuth();
  const { push } = useToast();
  const canMutate = user?.role !== 'viewer';

  const [people, setPeople] = useState<Person[] | null>(null);
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [bundle, setBundle] = useState<HygieneBundle | null>(null);
  const [approved, setApproved] = useState<DraftedFix[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanned, setScanned] = useState(false);

  useEffect(() => {
    const unsubPeople = subscribePeople(setPeople);
    const unsubAccounts = subscribeAccounts(setAccounts);
    return () => {
      unsubPeople();
      unsubAccounts();
    };
  }, []);

  const loading = people === null || accounts === null;
  const workerConfigured = argoWorker.isConfigured();

  /** Real worker seam: parse-or-throw → draftFix degrades to fallback. */
  const runWorker = useMemo(
    () =>
      workerConfigured
        ? async (prompt: string) => {
            const out = await argoWorker.runAgent(
              {
                id: 'crm-hygiene-drafter',
                name: 'CRM Hygiene Drafter',
                template: 'hygiene',
                persona: 'ops',
              },
              { prompt },
            );
            return JSON.parse(out.text) as { diff: ActionDiff; rationale: string };
          }
        : undefined,
    [workerConfigured],
  );

  const scan = async () => {
    if (loading || busy) return;
    setBusy(true);
    setError(null);
    try {
      const findings = scanHygiene(
        toHygieneAccounts(accounts!),
        toHygienePeople(people!),
        toTouches(people!),
        Date.now(),
        { staleDays: STALE_DAYS },
      );
      const drafts = await draftAll(findings, runWorker ? { runWorker } : {});
      setBundle(buildBundle(drafts, blastRadiusOf));
      setApproved([]);
      setScanned(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Hygiene scan failed.');
    } finally {
      setBusy(false);
    }
  };

  /** Route drafts through the EXISTING governed pipeline. Never a write. */
  const queueDrafts = async (drafts: DraftedFix[]) => {
    if (!bundle) return;
    setBusy(true);
    setError(null);
    try {
      for (const d of drafts) {
        for (const action of toActions(d.diff, people ?? [], accounts ?? [])) {
          await queueApproval(
            action,
            'autopilot',
            `CRM hygiene ${d.finding.kind} [${d.source}] ${stampOf(d)} — ${d.rationale}`,
          );
        }
      }
      let next = bundle;
      for (const d of drafts) next = rejectFinding(next, d.finding.id);
      setBundle(next);
      setApproved((prev) => [...prev, ...drafts]);
      push({
        kind: 'success',
        text: `${drafts.length} hygiene fix${drafts.length === 1 ? '' : 'es'} queued for approval.`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to queue fixes.');
    } finally {
      setBusy(false);
    }
  };

  const approveClusterAt = (index: number) => {
    if (!bundle) return;
    const diffs = approveCluster(bundle, index);
    const drafts = bundle.drafts.filter((d) => diffs.includes(d.diff));
    void queueDrafts(drafts);
  };

  /** Compile + queue the atomic inverse of everything approved so far. */
  const rollback = async () => {
    if (approved.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const approvedBundle = buildBundle(approved, blastRadiusOf);
      const plan = reversalPlan(approvedBundle);
      for (const diff of plan) {
        for (const action of toActions(diff, people ?? [], accounts ?? [])) {
          await queueApproval(
            action,
            'autopilot',
            `${REVERSAL_NOTE_PREFIX}${approvedBundle.id} — hygiene bundle rollback`,
          );
        }
      }
      setApproved([]);
      push({
        kind: 'success',
        text: `Rollback queued: ${plan.length} inverse diff${plan.length === 1 ? '' : 's'} awaiting approval.`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rollback failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <GlassPanel variant="subtle" animate={false} style={{ padding: '14px 18px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <span className="t-section-title" style={{ display: 'block', color: C.text }}>
            <ScanSearch size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
            CRM hygiene
          </span>
          <span className="t-meta" style={{ color: C.textMuted }}>
            Worker-drafted stale/dupe/next-step fixes, triaged into one reversible bundle.
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            className={workerConfigured ? 'glass-pill glass-pill--green' : 'glass-pill'}
            title={
              workerConfigured
                ? 'Fixes are drafted by the real Claude worker (with deterministic fallback).'
                : 'No worker configured — every fix uses the deterministic fallback drafter.'
            }
          >
            {workerConfigured ? <Zap size={11} /> : <CircleDashed size={11} />}{' '}
            {workerConfigured ? 'Claude worker' : 'Deterministic fallback'}
          </span>
          {approved.length > 0 && canMutate && (
            <button className="glass-btn" onClick={() => void rollback()} disabled={busy}>
              <Undo2 size={13} /> Rollback bundle
            </button>
          )}
          {canMutate && (
            <button
              className="glass-btn glass-btn--primary"
              onClick={() => void scan()}
              disabled={loading || busy}
            >
              <Sparkles size={13} /> {busy ? 'Working…' : scanned ? 'Re-scan' : 'Scan CRM'}
            </button>
          )}
        </div>
      </div>

      {/* States */}
      {error && (
        <p className="t-meta" style={{ margin: '10px 0 0', color: C.orange }}>
          <AlertTriangle size={12} style={{ verticalAlign: -2 }} /> {error}
        </p>
      )}
      {loading && (
        <p className="t-meta" style={{ margin: '10px 0 0', color: C.textMuted }}>
          Loading CRM snapshot…
        </p>
      )}
      {!loading && scanned && bundle && bundle.drafts.length === 0 && (
        <p className="t-meta" style={{ margin: '10px 0 0', color: C.textMuted }}>
          <Check size={12} style={{ verticalAlign: -2 }} /> CRM is clean — no stale
          stages, missing next steps, quiet deals or duplicates found.
        </p>
      )}

      {/* Risk-clustered triage */}
      {bundle && bundle.drafts.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          <span className="t-meta" style={{ color: C.textMuted }}>
            Bundle {bundle.id} · {bundle.drafts.length} drafted fix
            {bundle.drafts.length === 1 ? '' : 'es'}
          </span>
          {bundle.clusters.map((cluster, i) => {
            const drafts = bundle.drafts.filter((d) => cluster.diffs.includes(d.diff));
            const oneClick = cluster.severity !== 'high';
            return (
              <div
                key={cluster.severity}
                style={{
                  border: '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 12,
                  padding: '10px 12px',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className={SEVERITY_PILL[cluster.severity] ?? 'glass-pill'}>
                      {cluster.severity} risk
                    </span>
                    <span className="t-meta" style={{ color: C.textMuted }}>
                      {cluster.diffs.length} fix{cluster.diffs.length === 1 ? '' : 'es'} ·
                      blast radius {cluster.blastRadius}
                    </span>
                  </div>
                  {oneClick && canMutate && (
                    <button
                      className="glass-btn glass-btn--primary"
                      onClick={() => approveClusterAt(i)}
                      disabled={busy}
                      title="Queue every fix in this cluster through the approval pipeline"
                    >
                      <Check size={13} /> Approve all {cluster.diffs.length}
                    </button>
                  )}
                </div>

                <ol style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
                  {(compact ? drafts.slice(0, 4) : drafts).map((d) => (
                    <li
                      key={d.finding.id}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 10,
                        padding: '6px 0',
                        borderTop: '1px solid rgba(255,255,255,0.06)',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <span className="t-body" style={{ display: 'block', color: C.text }}>
                          {d.diff.summary}
                        </span>
                        <span
                          className="t-meta"
                          style={{ color: C.textMuted }}
                          title={d.finding.evidence.join('\n')}
                        >
                          {d.finding.kind} · {d.rationale}
                        </span>
                        <span className="t-meta" style={{ display: 'block', color: C.textMuted }}>
                          <span
                            className={d.source === 'worker' ? 'glass-pill glass-pill--green' : 'glass-pill'}
                            style={{ marginRight: 6 }}
                            title={
                              d.source === 'worker'
                                ? 'Drafted by the real Claude worker'
                                : 'Deterministic fallback draft — no worker needed'
                            }
                          >
                            {d.source === 'worker' ? <Zap size={10} /> : <RotateCcw size={10} />}{' '}
                            {d.source}
                          </span>
                          provenance {stampOf(d)}
                        </span>
                      </div>
                      {canMutate && (
                        <span style={{ display: 'inline-flex', gap: 6, flex: '0 0 auto' }}>
                          {!oneClick && (
                            <button
                              className="glass-btn"
                              onClick={() => void queueDrafts([d])}
                              disabled={busy}
                              title="Queue this single fix for approval"
                            >
                              <Check size={12} /> Approve
                            </button>
                          )}
                          <button
                            className="glass-btn"
                            onClick={() => setBundle(rejectFinding(bundle, d.finding.id))}
                            disabled={busy}
                            title="Reject this drafted fix"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      )}
                    </li>
                  ))}
                  {compact && drafts.length > 4 && (
                    <li className="t-meta" style={{ color: C.textMuted, padding: '6px 0 0' }}>
                      +{drafts.length - 4} more in this cluster…
                    </li>
                  )}
                </ol>
              </div>
            );
          })}
        </div>
      )}
    </GlassPanel>
  );
}
